import React, { useEffect, useState } from "react";
import moment from "moment";
import { Alert, AlertTitle } from "@mui/material";
import TravelRepository, { ComplianceAlertInfo } from "../../../Repositories/TravelRepository";
import ButtonField from "../../../Components/Ui/ButtonField";
import { useTailwindBrand } from "../../../Components/shared";
import { ENV_DECLARONS, getActivePrestationBrandConfig, isBrandActive } from "../../../config/environment.config";

interface Props {
    alert: ComplianceAlertInfo | null | undefined;
    /** Appelé après confirmation par le prof (le flag passe en confirmed_by_user). */
    onConfirmed?: (alert: ComplianceAlertInfo) => void;
    className?: string;
}

/**
 * P7 : bannière orange non bloquante « Distance inhabituelle détectée (environ
 * 12 km à pied), confirmez-vous ce déplacement ? ». Le prof peut confirmer en
 * un clic ; la bannière ne conditionne jamais la déclaration.
 */
const ComplianceAlert = ({ alert, onConfirmed, className = "" }: Props) => {
    const { brandConfig } = useTailwindBrand();
    const [current, setCurrent] = useState<ComplianceAlertInfo | null>(alert ?? null);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        setCurrent(alert ?? null);
    }, [alert]);

    if (!current || (current.status !== "open" && current.status !== "confirmed_by_user")) {
        return null;
    }

    const handleConfirm = async () => {
        setLoading(true);
        setFailed(false);
        try {
            const updated = await TravelRepository.confirmComplianceFlag(current.id);
            setCurrent(updated);
            onConfirmed && onConfirmed(updated);
        } catch (e) {
            console.warn("Confirmation du déplacement impossible", e);
            setFailed(true);
        } finally {
            setLoading(false);
        }
    };

    if (current.status === "confirmed_by_user") {
        return (
            <Alert severity="info" variant="outlined" className={className} sx={{ fontSize: "0.8rem", fontFamily: brandConfig.fontFamily }}>
                Déplacement {current.travelModeLabel.toLowerCase()} confirmé
                {current.confirmedAt ? ` le ${moment(current.confirmedAt).format("DD/MM/YYYY")}` : ""} malgré une distance estimée à environ{" "}
                {current.distanceKm != null ? current.distanceKm.toFixed(1) : "?"} km. Notre équipe pourra revenir vers vous si besoin.
            </Alert>
        );
    }

    return (
        <Alert
            severity="warning"
            variant="outlined"
            className={className}
            sx={{ fontSize: "0.85rem", fontFamily: brandConfig.fontFamily, alignItems: "flex-start" }}
        >
            <AlertTitle sx={{ fontFamily: brandConfig.fontFamily, fontWeight: 700 }}>Distance inhabituelle</AlertTitle>
            <p>{current.message}</p>
            <p className="text-xs text-gray-600 mt-1">
                La distance est estimée par le trajet routier. Votre {getActivePrestationBrandConfig()} n'est pas bloqué{isBrandActive(ENV_DECLARONS) ? "e" : ""} : cette
                confirmation sert uniquement à notre équipe. Si votre mode de déplacement a changé, corrigez-le depuis la fiche de votre client.
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
                <ButtonField
                    size="small"
                    variant="outlined"
                    color="warning"
                    label="Je confirme ce déplacement"
                    loading={loading}
                    disabled={loading}
                    onClick={handleConfirm}
                />
                {failed && <span className="text-xs text-red-600">La confirmation a échoué, réessayez plus tard.</span>}
            </div>
        </Alert>
    );
};

export default ComplianceAlert;
